import React, {useState, useEffect} from 'react';
import Sidebar from './Sidebar';
import PageHeader from './PageHeader';
import { useAuth } from '../contexts/AuthContext';
import { useDataCache } from '../contexts/DataCacheContext';

const DashboardLayout = ({children, currentSubPage}) => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const { logout, isAdmin } = useAuth();
    const { cache, prefetchSkills } = useDataCache();

    const faqEnabled = (cache.skills?.modules || []).some(
        m => m.module_id === 'faq_knowledge' && m.enabled
    );

    // Prefetch skills on mount so mobile menu can show Knowledge Base
    useEffect(() => {
        prefetchSkills();
    }, []);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 768) {
                setMobileMenuOpen(false);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <div className="flex min-h-screen" style={{background: '#F5F5F7'}}>
            <div className="hidden md:block">
                <Sidebar/>
            </div>

            {/* Mobile Menu Overlay */}
            {mobileMenuOpen && (
                <div className="md:hidden fixed inset-0 z-50 bg-black bg-opacity-50">
                    <div className="fixed inset-0 bg-white">
                        <div className="flex items-center justify-between p-4 border-b">
                            <h2 className="text-lg font-semibold">Menu</h2>
                            <button onClick={() => setMobileMenuOpen(false)} className="p-2">
                                <i className="fa fa-times text-xl"></i>
                            </button>
                        </div>
                        <nav className="p-4 space-y-2">
                            <a href="/dashboard" onClick={() => setMobileMenuOpen(false)}
                               className="flex items-center gap-3 px-4 py-3 rounded hover:bg-gray-100">
                                <i className="fa fa-chart-line text-sm"></i>
                                <span className="font-medium text-sm">Dashboard</span>
                            </a>
                            <a href="/assistant" onClick={() => setMobileMenuOpen(false)}
                               className="flex items-center gap-3 px-4 py-3 rounded hover:bg-gray-100">
                                <i className="fa fa-robot text-sm"></i>
                                <span className="font-medium text-sm">Assistant</span>
                            </a>
                            <a href="/staff" onClick={() => setMobileMenuOpen(false)}
                               className="flex items-center gap-3 px-4 py-3 rounded hover:bg-gray-100">
                                <i className="fa fa-users text-sm"></i>
                                <span className="font-medium text-sm">Staff</span>
                            </a>
                            <a href="/services" onClick={() => setMobileMenuOpen(false)}
                               className="flex items-center gap-3 px-4 py-3 rounded hover:bg-gray-100">
                                <i className="fa fa-briefcase text-sm"></i>
                                <span className="font-medium text-sm">Services</span>
                            </a>
                            <a href="/bookings" onClick={() => setMobileMenuOpen(false)}
                               className="flex items-center gap-3 px-4 py-3 rounded hover:bg-gray-100">
                                <i className="fa fa-calendar text-sm"></i>
                                <span className="font-medium text-sm">Bookings</span>
                            </a>
                            <a href="/smart-dispatch" onClick={() => setMobileMenuOpen(false)}
                               className="flex items-center gap-3 px-4 py-3 rounded hover:bg-gray-100">
                                <i className="fa fa-route text-sm"></i>
                                <span className="font-medium text-sm">Smart Dispatch</span>
                            </a>
                            <a href="/settings" onClick={() => setMobileMenuOpen(false)}
                               className="flex items-center gap-3 px-4 py-3 rounded hover:bg-gray-100">
                                <i className="fa fa-cog text-sm"></i>
                                <span className="font-medium text-sm">Settings</span>
                            </a>
                            <a href="/logs" onClick={() => setMobileMenuOpen(false)}
                               className="flex items-center gap-3 px-4 py-3 rounded hover:bg-gray-100">
                                <i className="fa fa-file-text text-sm"></i>
                                <span className="font-medium text-sm">Logs</span>
                            </a>
                            {faqEnabled && (
                                <a href="/faq-sources" onClick={() => setMobileMenuOpen(false)}
                                   className="flex items-center gap-3 px-4 py-3 rounded hover:bg-gray-100">
                                    <i className="fa fa-book text-sm"></i>
                                    <span className="font-medium text-sm">Knowledge Base</span>
                                </a>
                            )}
                            <a href="/skills" onClick={() => setMobileMenuOpen(false)}
                               className="flex items-center gap-3 px-4 py-3 rounded hover:bg-gray-100">
                                <i className="fa fa-puzzle-piece text-sm"></i>
                                <span className="font-medium text-sm">Skills</span>
                            </a>
                            <a href="/integrations" onClick={() => setMobileMenuOpen(false)}
                               className="flex items-center gap-3 px-4 py-3 rounded hover:bg-gray-100">
                                <i className="fa fa-plug text-sm"></i>
                                <span className="font-medium text-sm">Integrations</span>
                            </a>
                            <a href="/plans" onClick={() => setMobileMenuOpen(false)}
                               className="flex items-center gap-3 px-4 py-3 rounded hover:bg-gray-100">
                                <i className="fa fa-credit-card text-sm"></i>
                                <span className="font-medium text-sm">Subscription</span>
                            </a>
                            {isAdmin && (
                                <a href="/admin/customers" onClick={() => setMobileMenuOpen(false)}
                                   className="flex items-center gap-3 px-4 py-3 rounded hover:bg-gray-100">
                                    <i className="fa fa-users-cog text-sm"></i>
                                    <span className="font-medium text-sm">Admin</span>
                                </a>
                            )}
                            <div className="border-t my-4"></div>
                            <button onClick={logout}
                               className="flex items-center gap-3 px-4 py-3 rounded hover:bg-gray-100 w-full text-left">
                                <i className="fa fa-sign-out text-sm"></i>
                                <span className="font-medium text-sm">Sign Out</span>
                            </button>
                        </nav>
                    </div>
                </div>
            )}

            <main className="flex-1 md:ml-60 min-h-screen overflow-x-hidden" style={{fontFamily: 'Inter', background: '#F5F5F7'}}>
                <div className="pt-4 pr-4 pb-4 pl-2 md:pt-8 md:pr-8 md:pb-8 md:pl-4">
                    <PageHeader mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen}
                                currentSubPage={currentSubPage}/>
                    {children}
                </div>
            </main>
        </div>
    );
};

export default DashboardLayout;